import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Plus, Trash2, Filter, Calendar } from 'lucide-react';
import { listCustomQuotes, createCustomQuote, deleteCustomQuote } from '../../api/customQuotesAdmin';
import { getAdminCompanies } from '../../api/admin';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Input, Select, Textarea } from '../../components/ui/Input';

const STATUS_LABELS = {
  pending: 'Beklemede',
  accepted: 'Kabul Edildi',
  expired: 'Süresi Doldu',
  rejected: 'Reddedildi',
  cancelled: 'İptal Edildi',
};
const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  accepted: 'bg-green-100 text-green-700',
  expired: 'bg-slate-100 text-slate-500',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

export function CustomQuotesAdminPage() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');

  // Create
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    company_id: '',
    monthly_amount: '',
    yearly_amount: '',
    max_users: '',
    description: '',
    valid_days: 30,
  });
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState('');

  // Delete confirm
  const [deletingQuote, setDeletingQuote] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const d = await listCustomQuotes(params);
      let qs = d.quotes || [];
      if (filterStatus) qs = qs.filter(q => q.status === filterStatus);
      setQuotes(qs);
    } catch {
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    getAdminCompanies({ limit: 200 }).then(d => setCompanies(d.companies || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    if (!createForm.company_id || !createForm.max_users) {
      return setCreateError('Firma ve maksimum kullanıcı zorunlu');
    }
    if (!createForm.monthly_amount && !createForm.yearly_amount) {
      return setCreateError('En az bir tutar (aylık veya yıllık) gerekli');
    }
    setCreateSaving(true);
    try {
      const validUntil = new Date();
      validUntil.setDate(validUntil.getDate() + parseInt(createForm.valid_days, 10));

      await createCustomQuote({
        company_id: createForm.company_id,
        max_users: parseInt(createForm.max_users, 10),
        monthly_amount: createForm.monthly_amount ? parseFloat(createForm.monthly_amount) : null,
        yearly_amount: createForm.yearly_amount ? parseFloat(createForm.yearly_amount) : null,
        description: createForm.description || null,
        valid_until: validUntil.toISOString(),
      });
      setCreateOpen(false);
      setCreateForm({
        company_id: '', monthly_amount: '', yearly_amount: '',
        max_users: '', description: '', valid_days: 30,
      });
      await load();
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Oluşturulamadı');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingQuote) return;
    setDeleteSaving(true);
    try {
      await deleteCustomQuote(deletingQuote.id);
      setDeletingQuote(null);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Silinemedi');
    } finally {
      setDeleteSaving(false);
    }
  };

  const formatTL = (n) => n != null
    ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(n)
    : '—';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '—';

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/ops-center-f9x2')}
              className="text-slate-500 hover:text-slate-700"
            >
              <ArrowLeft size={20} />
            </button>
            <FileText size={22} className="text-amber-600" />
            <h1 className="text-xl font-bold text-slate-800">Özel Teklifler</h1>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus size={16} /> Yeni Teklif
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {/* Filtreler */}
        <div className="flex items-center gap-3 mb-4">
          <Filter size={16} className="text-slate-400" />
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-48"
          >
            <option value="">Tüm durumlar</option>
            <option value="pending">Beklemede</option>
            <option value="accepted">Kabul Edildi</option>
            <option value="expired">Süresi Doldu</option>
            <option value="cancelled">İptal Edildi</option>
          </Select>
        </div>

        {loading && <p className="text-slate-500">Yükleniyor...</p>}

        {!loading && quotes.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <FileText size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 mb-4">Teklif bulunamadı</p>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus size={16} /> İlk Teklifi Oluştur
            </Button>
          </div>
        )}

        {!loading && quotes.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-slate-600 font-semibold">Firma</th>
                  <th className="text-right px-4 py-3 text-slate-600 font-semibold">Maks. Kull.</th>
                  <th className="text-right px-4 py-3 text-slate-600 font-semibold">Aylık</th>
                  <th className="text-right px-4 py-3 text-slate-600 font-semibold">Yıllık</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-semibold">Geçerlilik</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-semibold">Durum</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-semibold">Açıklama</th>
                  <th className="text-right px-4 py-3 text-slate-600 font-semibold">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map(q => (
                  <tr key={q.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-3 text-slate-800 font-medium">{q.company_name || q.company_id}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{q.max_users}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-800">{formatTL(q.monthly_amount)}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{formatTL(q.yearly_amount)}</td>
                    <td className="px-4 py-3 text-slate-700 text-xs">
                      <Calendar size={11} className="inline mr-1" />
                      {formatDate(q.valid_until)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[q.status] || 'bg-slate-100 text-slate-500'}`}>
                        {STATUS_LABELS[q.status] || q.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs max-w-xs truncate">{q.description || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      {q.status === 'pending' && (
                        <button
                          onClick={() => setDeletingQuote(q)}
                          className="text-red-600 hover:underline text-xs"
                        >
                          <Trash2 size={12} className="inline mr-1" /> İptal
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Yeni Özel Teklif" size="lg">
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Firma *</label>
            <Select
              value={createForm.company_id}
              onChange={(e) => setCreateForm(f => ({ ...f, company_id: e.target.value }))}
              required
            >
              <option value="">Seçin...</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Maks. Kullanıcı *</label>
            <Input
              type="number"
              min="1"
              value={createForm.max_users}
              onChange={(e) => setCreateForm(f => ({ ...f, max_users: e.target.value }))}
              placeholder="100"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Aylık Tutar (TL)</label>
              <Input
                type="number"
                step="0.01"
                value={createForm.monthly_amount}
                onChange={(e) => setCreateForm(f => ({ ...f, monthly_amount: e.target.value }))}
                placeholder="5000"
              />
              <p className="text-xs text-slate-500 mt-1">KDV hariç</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Yıllık Tutar (TL)</label>
              <Input
                type="number"
                step="0.01"
                value={createForm.yearly_amount}
                onChange={(e) => setCreateForm(f => ({ ...f, yearly_amount: e.target.value }))}
                placeholder="48000 (opsiyonel)"
              />
              <p className="text-xs text-slate-500 mt-1">En az aylık veya yıllık zorunlu</p>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Açıklama</label>
            <Textarea
              value={createForm.description}
              onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Teklif detayları, özel şartlar..."
              rows={3}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Geçerlilik Süresi (gün)</label>
            <Input
              type="number"
              min="1"
              value={createForm.valid_days}
              onChange={(e) => setCreateForm(f => ({ ...f, valid_days: e.target.value }))}
              placeholder="30"
            />
          </div>

          {createError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
              {createError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="secondary" type="button" onClick={() => setCreateOpen(false)}>Vazgeç</Button>
            <Button type="submit" loading={createSaving}>Teklifi Oluştur</Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirm */}
      <Modal open={!!deletingQuote} onClose={() => setDeletingQuote(null)} title="Teklifi İptal Et" size="md">
        <div className="space-y-4">
          <p className="text-sm text-slate-700">
            <strong>{deletingQuote?.company_name}</strong> için
            {' '}{formatTL(deletingQuote?.monthly_amount)} aylık tutarındaki teklifi iptal etmek istediğinize emin misiniz?
          </p>
          <p className="text-xs text-slate-500">Bu işlem geri alınamaz. Müşteri artık bu teklifi kabul edemez.</p>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="secondary" onClick={() => setDeletingQuote(null)}>Vazgeç</Button>
            <Button variant="danger" onClick={handleDelete} loading={deleteSaving}>
              <Trash2 size={16} /> İptal Et
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
